import { z } from 'zod'

const rfc = z.string()

/** One reset session: the chair, the headphones, a timer. */
export const dayReset = z
  .object({
    /** RFC 3339 */
    start: rfc,
    size: z.enum(['full', 'short', 'minimum']),
    audio: z.enum(['noise', 'none']).optional(),
    /** the timer ran out, rather than being stopped */
    completed: z.boolean().optional(),
    ended: rfc.optional(),
  })
  .strict()
export type DayReset = z.infer<typeof dayReset>

/** One entry in the smoking log, in the owner's own unit. */
export const daySmoke = z.object({ at: rfc, amount: z.number().min(0).max(1000) }).strict()
export type DaySmoke = z.infer<typeof daySmoke>

/** The sizes a reset comes in, with their minutes (mirrors internal/kinds/day). */
export const RESET_SIZES = { full: 30, short: 10, minimum: 5 } as const
export type ResetSize = keyof typeof RESET_SIZES

/**
 * A Day is one record per local calendar date with what the owner declared
 * about it: when they woke and slept (the bookend buttons), and the wellbeing
 * module's evening: the check-in on fixed 0–10 scales, "End of day", the
 * reset sessions, the smoking log, and one timed alarm the server fires.
 * No actions, no status of its own, no streaks.
 */
export const daySpec = z
  .object({
    /** local calendar date, YYYY-MM-DD */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** RFC 3339 UTC */
    woke_at: rfc.optional(),
    slept_at: rfc.optional(),
    /** the check-in, 0–10 on scales that never change; absent until answered */
    anxiety: z.number().int().min(0).max(10).optional(),
    unreality: z.number().int().min(0).max(10).optional(),
    mood: z.number().int().min(0).max(10).optional(),
    nap_min: z.number().int().min(0).max(1440).optional(),
    exercise_min: z.number().int().min(0).max(1440).optional(),
    note: z.string().max(500).optional(),
    checked_in_at: rfc.optional(),
    /** "End of day" pressed: the event that starts the evening */
    ended_at: rfc.optional(),
    resets: z.array(dayReset).max(20).optional(),
    smokes: z.array(daySmoke).max(100).optional(),
    /** one timed push: the end of a reset, the nap cap */
    alarm_at: rfc.optional(),
    alarm_text: z.string().max(200).optional(),
    alarm_url: z.string().max(200).regex(/^\//).optional(),
  })
  .strict()
export type DaySpec = z.infer<typeof daySpec>

export const dayStatus = z.object({}).passthrough()
export type DayStatus = z.infer<typeof dayStatus>
